import React from 'react';
import { useSelector } from 'react-redux';
import { toBackendLang } from '@plone/volto/helpers/Utils/Utils';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import RichTextWidget from '@plone/volto/components/theme/Widgets/RichTextWidget';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import BookingButton from '../../Cart/BookingButton';
import SeminareExpanded, {
  formatBookingPrice,
  formatDate,
  sumPrices,
  type ListingItem,
} from './SeminareExpanded';

type SeminarListingProps = {
  items: ListingItem[];
};

const SeminarListing = ({ items }: SeminarListingProps) => {
  const lang = useSelector(
    (state: { intl: { locale: string } }) => state.intl.locale,
  );
  const locale = toBackendLang(lang);
  const [openItems, setOpenItems] = React.useState<Record<string, boolean>>({});

  const toggleItem = (itemKey: string) => {
    setOpenItems((current) => ({
      ...current,
      [itemKey]: !current[itemKey],
    }));
  };

  return (
    <div className="seminar-listing">
      {items.map((item, index) => {
        const itemKey = item['@id'] || item.id || `seminar-${index}`;
        const title = item.thema || item.title || item.id || 'Seminar';
        const isOpen = Boolean(openItems[itemKey]);
        const startDate = formatDate(item.start_date, locale);
        const accommodationPrice = sumPrices(
          item.hotelpreis_tagungspauschale,
          item.hotelpreis_verpflegung,
        );
        const totalPrice = sumPrices(item.seminargebuehr, accommodationPrice);
        const bookingPrice = formatBookingPrice(
          totalPrice || item.seminargebuehr,
        );
        const bookingType =
          item.technical_type === 'muenchenprogramm' ? 'Kurs' : 'Seminar';
        const seminarId = item.UID;

        return (
          <article
            className={`seminar-listing-item${isOpen ? ' is-open' : ''}`}
            key={itemKey}
          >
            <div className="seminare-card">
              <div className="seminare-card-meta">
                <p className="seminare-kicker">
                  {startDate && <>Termin {startDate}</>}
                </p>
              </div>
              <h3>{title}</h3>
              <p className="seminare-teaser">
                {item.thema_zusatz && (
                  <span className="seminare-topic-addition">
                    {item.thema_zusatz}
                  </span>
                )}
                {item.description && (item.nummer || item.thema_zusatz) && (
                  <> | </>
                )}
                {item.description}
                <br />
                {item.nummer && <>Seminar-Nr. {item.nummer} </>}
              </p>
              {!isOpen && item.programmtext?.data && (
                <div className="seminare-program-preview">
                  <RichTextWidget
                    className="seminare-program-preview-text"
                    value={item.programmtext}
                  />
                </div>
              )}
              <div className="seminare-card-actions">
                <button
                  aria-expanded={isOpen}
                  aria-controls={`${itemKey}-content`}
                  className="seminare-read-more"
                  type="button"
                  onClick={() => toggleItem(itemKey)}
                >
                  <span aria-hidden="true" className="seminare-chevron" />
                  <span className="read-more">
                    {isOpen ? 'Weniger lesen' : 'Mehr lesen'}
                  </span>
                </button>
                {!isOpen && bookingPrice && (
                  <BookingButton
                    className="seminare-booking-button"
                    seminarId={seminarId}
                    courseNumber={item.nummer}
                    topic={item.thema}
                    startDate={item.start_date}
                    endDate={item.end_date}
                    price={totalPrice || item.seminargebuehr}
                    technicalType={item.technical_type}
                  >
                    {bookingType} für {bookingPrice} buchen
                  </BookingButton>
                )}
              </div>
            </div>

            <SeminareExpanded
              isOpen={isOpen}
              item={item}
              itemKey={itemKey}
              onClose={() => toggleItem(itemKey)}
            />
          </article>
        );
      })}
    </div>
  );
};

export default SeminarListing;
